import { DateTime } from 'luxon';

export const OPENING_HOURS_TIME_ZONE = 'America/New_York';

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const toDateTimeInOpeningHoursZone = (dateInput) => {
  if (!dateInput) return null;

  if (DateTime.isDateTime(dateInput)) {
    return DateTime.fromObject(
      {
        year: dateInput.year,
        month: dateInput.month,
        day: dateInput.day,
        hour: dateInput.hour,
        minute: dateInput.minute,
        second: dateInput.second,
        millisecond: dateInput.millisecond,
      },
      { zone: OPENING_HOURS_TIME_ZONE }
    );
  }

  if (dateInput instanceof Date) {
    return DateTime.fromObject(
      {
        year: dateInput.getFullYear(),
        month: dateInput.getMonth() + 1,
        day: dateInput.getDate(),
        hour: dateInput.getHours(),
        minute: dateInput.getMinutes(),
        second: dateInput.getSeconds(),
        millisecond: dateInput.getMilliseconds(),
      },
      { zone: OPENING_HOURS_TIME_ZONE }
    );
  }

  if (typeof dateInput === 'string') {
    if (DATE_ONLY_PATTERN.test(dateInput)) {
      return DateTime.fromISO(dateInput, { zone: OPENING_HOURS_TIME_ZONE });
    }

    const parsedDate = DateTime.fromISO(dateInput, { setZone: true });

    if (parsedDate.isValid) {
      return DateTime.fromObject(
        {
          year: parsedDate.year,
          month: parsedDate.month,
          day: parsedDate.day,
          hour: parsedDate.hour,
          minute: parsedDate.minute,
          second: parsedDate.second,
          millisecond: parsedDate.millisecond,
        },
        { zone: OPENING_HOURS_TIME_ZONE }
      );
    }

    return DateTime.fromISO(dateInput, { zone: OPENING_HOURS_TIME_ZONE });
  }

  return null;
};

const getDatePriority = (hours) => {
  if (!hours?.type || hours.type === 'day') return 3;
  if (hours.type === 'range') return 2;
  if (hours.type === 'holiday') return 1;
  return 0;
};

const matchesIrregularHours = (hours, date) => {
  if (!hours || !date) return false;

  if (hours.type === 'holiday') {
    return (
      hours.holidayMonth + 1 === date.month && hours.holidayDate === date.day
    );
  }

  if (hours.type === 'range') {
    const startDate = toDateTimeInOpeningHoursZone(hours.startDate)?.startOf(
      'day'
    );
    const endDate = toDateTimeInOpeningHoursZone(hours.endDate)?.startOf('day');

    return Boolean(
      startDate && endDate && date >= startDate && date <= endDate
    );
  }

  return hours.date === date.toISODate();
};

const hasValidHoursRange = (hours) =>
  [
    hours?.openHour,
    hours?.openMinute,
    hours?.closeHour,
    hours?.closeMinute,
  ].every(Number.isFinite);

export const isClosedHours = (hours) =>
  !hours || Boolean(hours.isClosed) || !hasValidHoursRange(hours);

export const getOpeningHoursForDate = (openingHours, dateInput) => {
  const date = toDateTimeInOpeningHoursZone(dateInput)?.startOf('day');

  if (!date || !openingHours) return null;

  const irregularHours =
    openingHours.irregular
      ?.filter((hours) => matchesIrregularHours(hours, date))
      .sort((a, b) => getDatePriority(b) - getDatePriority(a))[0] || null;

  if (irregularHours) return irregularHours;

  const weekday = date.toFormat('cccc').toLowerCase();

  return (
    openingHours.regular?.find(
      (hours) => hours.weekday?.toLowerCase() === weekday
    ) || null
  );
};

export const formatHoursRange = (hours, dateInput) => {
  const date = toDateTimeInOpeningHoursZone(dateInput)?.startOf('day');

  if (!date || isClosedHours(hours)) return '';

  const startTime = date
    .set({
      hour: hours.openHour,
      minute: hours.openMinute,
    })
    .toFormat('h:mm a');

  const endTime = date
    .set({
      hour: hours.closeHour,
      minute: hours.closeMinute,
    })
    .toFormat('h:mm a');

  return `${startTime} - ${endTime}`;
};

export const formatOpeningHoursForDate = (
  openingHours,
  dateInput,
  closedLabel = 'Closed'
) => {
  if (openingHours?.temporarilyClosed) return closedLabel;

  const hours = getOpeningHoursForDate(openingHours, dateInput);

  return isClosedHours(hours)
    ? closedLabel
    : formatHoursRange(hours, dateInput) || closedLabel;
};

export const isVenueDateUnavailable = (
  venue,
  dateInput,
  unavailableDates = []
) => {
  const date = toDateTimeInOpeningHoursZone(dateInput)?.startOf('day');

  if (!date) return false;

  if (venue?.openingHours?.temporarilyClosed) {
    return true;
  }

  const isoDate = date.toISODate();

  if (unavailableDates.includes(isoDate)) {
    return true;
  }

  const startDate = venue?.startDate
    ? toDateTimeInOpeningHoursZone(venue.startDate)?.startOf('day')
    : null;
  const endDate = venue?.endDate
    ? toDateTimeInOpeningHoursZone(venue.endDate)?.startOf('day')
    : null;

  if ((startDate && date < startDate) || (endDate && date > endDate)) {
    return true;
  }

  return isClosedHours(getOpeningHoursForDate(venue?.openingHours, date));
};
